import { LineSegment, VictoryArea, VictoryAxis, VictoryChart, VictoryContainer, VictoryLabel, VictoryStack, VictoryTooltip, VictoryVoronoiContainer, } from "victory";
import { AnimalInfo, FeedCurve } from "../../../Services";
import { ComponentAmount, getMixtureDataPerComponent } from "./_FeedCurveViewer/getMixtureDataPerComponent";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { linearProjection } from "./_FeedCurveViewer/linearProjection";
import { colors, fonts, getGraphColor } from "../../../Styles";
import { SHOULD_DISPLAY_CONTINUES } from "../../FeedCurves/_FeedCurves/feedCurveTableUtils";
import constructDataMap from "./_FeedCurveViewer/constructDataMap";
import { getMilestoneLines } from "./SubComponents";
import { FeedCurveMilestone } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { getCurrentMilestone } from "./_FeedCurveViewer/getCurrentMilestone";
import { GraphTypes, useFeedCurveContext } from "../../FeedCurves/_FeedCurves/feedCurveContext";

export type MilestoneClick = {
  milestoneIndex: number; milestoneName: string; lastMilestoneDay: number, relativeX: number
}

interface FeedCurveGraphProps {
  data: FeedCurve,
  invertedColors?: boolean
  isMiniature?: boolean
  milestones?: {
    showMilestones?: boolean,
    showMilestoneToolTip?: boolean,
    animals?: AnimalInfo[]
  }
  dataMapConstruction?: {
    dataMap: Map<number, ComponentAmount[]>;
    lastDay: number;
    firstDay: number;
    maxAmount: number;
    minAmount: number;
    milestonesFirstDay: number[];
  }}

export default function FeedCurveGraph({ data: feedCurve, dataMapConstruction, invertedColors = false, isMiniature=false, milestones }: FeedCurveGraphProps) {
  const { t } = useTranslation();
  const { state, dispatch } = useFeedCurveContext();
  const { dataMap, lastDay, firstDay, maxAmount, minAmount, milestonesFirstDay } = dataMapConstruction ?? constructDataMap(cloneDeep(feedCurve));
  
  const amountOfDays = lastDay - firstDay;
  const extendedMaxAmount = maxAmount * 1.05;

  const [hoveredMilestone, setHoveredMilestone] = useState<{ selectedMilestone: FeedCurveMilestone; selectedDay: number } | null>(null);

  const _feedCurveWidth = isMiniature ? 300 : 600;
  const _chartWidth = isMiniature ? 300 : 550;
  const _chartHeight = isMiniature ? 150 : 300;
  const _fontSize = { ...fonts.fontSize4 };
  const _primaryColor = colors.greyPrimary

  const handleClick = () => {
    if (hoveredMilestone) {
      dispatch({milestone: {selectedMilestone: hoveredMilestone.selectedMilestone, selectedDay: hoveredMilestone.selectedDay}})
    }
  };

  // #region | Victory Voronoi Container
  const victoryVoronoiContainer = milestones&&milestones.showMilestoneToolTip ? (
    <VictoryVoronoiContainer
      voronoiDimension="x"
      onActivated={(points) => {
        if (!points || !points.length) return;
        const xValue = points[0].x;
      
        const { milestone, relativeX } = getCurrentMilestone(xValue, feedCurve.milestones ?? []);
      
        if (milestone) {
          setHoveredMilestone({
            selectedMilestone: milestone,
            selectedDay: relativeX
          });
        }
      }}
      style={{ cursor: "pointer" }}
    />
  ) : <VictoryContainer/>;
  // #endregion

  // #region | Milestone Tooltip
  const Flyout = (props: any) => {
    const { x } = props;
  
    return ( <line x1={x} y1={0+15} x2={x} y2={_chartHeight-20} stroke={colors.greyLightTertiary} strokeWidth={2} /> );
  };

  const FixedLabelTooltip = (props: any) => {
    const { datum } = props;
    
    const xValue = datum.x;
    const { milestone, relativeX, sorted } = getCurrentMilestone(xValue, feedCurve.milestones ?? []);
    const milestoneIndex = milestone ? sorted.findIndex(m => m.feedCurveMilestoneId === milestone.feedCurveMilestoneId) + 1 : 0;
    
    const text =
        milestone
        ? `M${milestoneIndex}: ${milestone?.name} (${t("feedCurvePages.feedCurve.day", {defaultValue: "day"})} ${relativeX})`
        : `${t("feedCurvePages.feedCurve.unknownMilestoneAndDay", {defaultValue: "Unknown milestone and day"})}`;
  
    return (
      <>
        <Flyout {...props} />
        <text
          x={50}
          y={7}
          style={{
            ...fonts.paragraph,
            ..._fontSize,
            fill: colors.greyLightTertiary,
            pointerEvents: "none",
          }}
        >
          {text}
        </text>
      </>
    );
  };

  const tooltipData = Array.from({ length: lastDay - firstDay + 1 }, (_, i) => ({
    x: firstDay + i,
    y: 0
  }));

  const tooltipSeries = (
    <VictoryArea
      data={tooltipData}
      labels={() => ""}
      labelComponent={<VictoryTooltip flyoutComponent={<FixedLabelTooltip/>} />}
      style={{ data: { fill: colors.transparent, pointerEvents: "all", cursor: "pointer", zIndex: 9999} }}
    />
  );
  // #endregion

  // #region | Victory Area
  const victoryArea = useMemo(() => {
    const barData = getMixtureDataPerComponent(dataMap, linearProjection, amountOfDays);
  
    return Array.from(dataMap.keys()).map((id, index) => {
      const selectedComponent = state.component;
      const hasHover = state.graphType === GraphTypes.feedCurveWithLabels
      
      return (
        <VictoryArea
          interpolation={amountOfDays > 250 ? "linear" : "stepAfter"}
          key={index}
          data={barData.get(id)}
          x="x"
          y="y"
          style={{
            data: {
              fill: hasHover && (selectedComponent && selectedComponent === id) ? colors.greyTertiary : getGraphColor(id),
              stroke: "none",
              transition: "0.3s",
              zIndex: 2
            },
          }}
          events={[{
            target: "data",
            eventHandlers: {
              onMouseEnter: () => hasHover && dispatch({ component: id }),
              onMouseLeave: () => hasHover && dispatch({ component: undefined })
            },
          }]}
        />
      );
    })
  }, [dataMap, amountOfDays, state.component, state.graphType, dispatch])
  // #endregion



  return ( <>
    <div onClick={ milestones&&milestones.showMilestoneToolTip ? handleClick : undefined} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", position: "relative" }}>          
      <div style={{ width: _feedCurveWidth, margin: "0 auto"}} >
        <VictoryChart
          padding={{ top: 20, bottom: 25, left: 50, right: 40 }}
          width={_chartWidth}
          height={_chartHeight}
          containerComponent={milestones && milestones.showMilestoneToolTip ? victoryVoronoiContainer : <VictoryContainer /> }
        >
          
          <VictoryStack>
            {victoryArea}
            {milestones&&milestones.showMilestoneToolTip ? tooltipSeries : undefined}
          </VictoryStack>

          {/* X-Axis: Days */}
          <VictoryAxis
            offsetY={25}
            orientation="bottom"
            tickFormat={(x: any) => { 
              const firstMapArray = dataMap.values().next().value;
              const infimum = firstMapArray?.filter(({day}) => (x >= day))?.at(-1);
              return infimum?.day && !SHOULD_DISPLAY_CONTINUES ? `${x-infimum.dayOffset}` : `${x}`}
            }
            standalone={false}
            tickCount={Math.min(10, amountOfDays)}
            domain={[firstDay, lastDay+1]}
            style={{
              ticks: { stroke: _primaryColor, size: 5 },
              axis: { stroke: _primaryColor, strokeWidth: 2 }
            }}
            crossAxis={false}
            label={t("units.days", {defaultValue: "Days"})}
            tickLabelComponent={
              <VictoryLabel
                y={_chartHeight}
                verticalAnchor="end"
                style={{
                  ...fonts.paragraph,
                  ..._fontSize,
                  fill: _primaryColor,
                }}
              />
            }
            axisLabelComponent={
              <VictoryLabel
                y={_chartHeight} 
                x={_chartWidth}
                verticalAnchor="end"
                textAnchor="end"
                style={{
                  ...fonts.header,
                  ..._fontSize,
                  fill: _primaryColor,
                }}
              />
            }
            axisComponent={
              <LineSegment x1={35} x2={_chartWidth} />
            }
          />
          
          {/* Y-Axis: Amount */}
          <VictoryAxis
            offsetX={40}
            dependentAxis
            domain={[minAmount, extendedMaxAmount]}
            standalone={false}
            tickCount={ isMiniature ? 4 : 8}
            tickFormat={(y:any) => y === maxAmount ? `${maxAmount}` : `${y}`}
            style={{
              ticks: { stroke: _primaryColor, size: 5 },
              axis: { stroke: _primaryColor, strokeWidth: 2 },
            }}
            crossAxis={false}
            label={t("units.kg", { defaultValue: "KG" })}
            tickLabelComponent={
              <VictoryLabel
                textAnchor="end"   
                style={{
                  ...fonts.paragraph,
                  ..._fontSize,
                  fill: _primaryColor,
                }}
              />
            }
            axisLabelComponent={
              <VictoryLabel
                angle={0}
                verticalAnchor="start" 
                textAnchor="start"   
                x={8}               
                y={0}  
                style={{
                  ...fonts.header,
                  ..._fontSize,
                  fill: _primaryColor,
                }}
              />
            }
            axisComponent={<LineSegment y1={0} y2={_chartHeight-20}/>}
          />
          
          {/* Milestones */}
          {milestones&&milestones.showMilestones ? 
            getMilestoneLines({
              feedCurve,
              milestonesFirstDay: milestonesFirstDay,
              extendedMaxAmount,
              firstDay: firstDay,
              lastDay: lastDay,
              overrides: {
                fontSize: _fontSize,
                invertedColors: invertedColors,
                width: _feedCurveWidth 
              },
            })
          : undefined}
          
        </VictoryChart>
      </div>
    </div>
  </> );
}

interface FixedLabelTooltipProps {
  feedCurveMilestones?: FeedCurveMilestone[];
  chartHeight: number;
}

const FlyoutLine = ({ x, chartHeight }: { x: number; chartHeight: number }) => (
  <line
    x1={x}
    y1={15}
    x2={x}
    y2={chartHeight - 20}
    stroke={colors.greyLightTertiary}
    strokeWidth={2}
  />
);

export const FixedLabelTooltip = ({
  feedCurveMilestones,
  chartHeight,
}: FixedLabelTooltipProps) => {
  const CustomFlyout = (props: any) => {
    const { datum } = props;
    const xValue = datum.x;
    const { milestone, relativeX, sorted } = getCurrentMilestone(xValue, feedCurveMilestones ?? []);
    const milestoneIndex = milestone
      ? sorted.findIndex((m) => m.feedCurveMilestoneId === milestone.feedCurveMilestoneId) + 1
      : 0;

    const text = milestone
      ? `M${milestoneIndex}: ${milestone.name} (day ${relativeX})`
      : `Unknown milestone`;

    return (
      <>
        <FlyoutLine x={props.x} chartHeight={chartHeight} />
        <text
          x={50}
          y={7}
          style={{
            ...fonts.paragraph,
            ...fonts.fontSize4,
            fill: colors.greyLightTertiary,
          }}
        >
          {text}
        </text>
      </>
    );
  };

  return <VictoryTooltip flyoutComponent={<CustomFlyout />} />;
};